import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface Offer {
    id: number;
    loan_amount: string | number;
    tenure: number;
    interest_rate: string | number;
    emi: string | number;
    processing_fee: string | number;
    display_flag: boolean;
    display_order?: number | null;
    offer_selected: boolean;
    rejected_by_le: boolean;
    repay_type?: string;
}

interface Lender {
    lender_id: number;
    lender_name: string;
    lender_logo?: string;
    lender_description?: string;
    lender_terms?: string;
    offers: Offer[];
}

interface LoanOfferCardProps {
    lender: Lender;
    selectedOfferId: number | null;
    onSelectOffer: (offerId: number) => void;
}

const LoanOfferCard: React.FC<LoanOfferCardProps> = ({ lender, selectedOfferId, onSelectOffer }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    // Find the offer to display when collapsed (prioritize display_flag, else first one)
    const featuredOffer = lender.offers.find(o => o.display_flag) || lender.offers[0];

    // If there are no offers, don't render anything (sanity check)
    if (!lender.offers.length) return null;

    return (
        <Card className={`mb-6 shadow-md transition-shadow duration-300 border border-gray-100 ${isExpanded ? 'ring-1 ring-blue-100' : ''}`}>
            <CardHeader className="flex flex-row items-center gap-4 bg-gray-50/50 rounded-t-xl pb-4">
                {lender.lender_logo && (
                    <div className="w-16 h-16 relative overflow-hidden rounded-lg bg-white p-1 border">
                        <img
                            src={lender.lender_logo}
                            alt={`${lender.lender_name} logo`}
                            className="object-contain w-full h-full"
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                            }}
                        />
                    </div>
                )}
                <div className="flex-1">
                    <CardTitle className="text-xl font-bold text-gray-800">{lender.lender_name}</CardTitle>
                    {lender.lender_description && <CardDescription className="text-gray-500 mt-1">{lender.lender_description}</CardDescription>}
                </div>
                {lender.offers.length > 1 && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                        {isExpanded ? 'Show Less' : `View all ${lender.offers.length} offers`}
                    </Button>
                )}
            </CardHeader>
            <CardContent className="pt-6 grid gap-4">
                {isExpanded ? (
                    // Expanded View: Show ALL offers
                    lender.offers.map((offer) => (
                        <OfferRow
                            key={offer.id}
                            offer={offer}
                            isSelected={selectedOfferId === offer.id}
                            onSelect={() => onSelectOffer(offer.id)}
                        />
                    ))
                ) : (
                    // Collapsed View: Show only FEATURED offer
                    <div className="relative">
                        <div className="absolute -top-3 left-0 bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full font-medium border border-yellow-200 shadow-sm z-10">
                            Popular Offer
                        </div>
                        <OfferRow
                            offer={featuredOffer}
                            isSelected={selectedOfferId === featuredOffer.id}
                            onSelect={() => onSelectOffer(featuredOffer.id)}
                        />
                    </div>
                )}

                {lender.lender_terms && (
                    <p className="text-xs text-gray-400 mt-2 italic px-2">
                        * {lender.lender_terms}
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

// Helper component for cleaner code
const OfferRow: React.FC<{ offer: Offer; isSelected: boolean; onSelect: () => void }> = ({ offer, isSelected, onSelect }) => {
    return (
        <div
            className={`
                relative flex flex-col md:flex-row md:items-center justify-between p-4 rounded-xl border-2 transition-all duration-200
                ${offer.rejected_by_le
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : isSelected
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20 cursor-pointer'
                        : 'border-gray-100 hover:border-blue-200 hover:bg-blue-50/50 cursor-pointer'}
            `}
            onClick={() => !offer.rejected_by_le && onSelect()}
        >
            {offer.rejected_by_le && (
                <Badge variant="destructive" className="absolute -top-3 left-36 shadow-sm text-[10px] font-bold uppercase tracking-wider">
                    Offer Rejected by Lender
                </Badge>
            )}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 md:mb-0">
                <div className="space-y-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Loan Amount</p>
                    <p className="font-bold text-lg text-gray-900">₹{parseFloat(offer.loan_amount.toString()).toLocaleString('en-IN')}</p>
                </div>
                <div className="space-y-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Tenure</p>
                    <p className="font-semibold text-gray-900">{offer.tenure} {offer.repay_type === 'EDI' ? 'Days' : 'Months'}</p>
                </div>
                <div className="space-y-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Interest</p>
                    <p className={`font-semibold ${offer.rejected_by_le ? 'text-gray-500' : 'text-green-600'}`}>{offer.interest_rate}% / mo</p>
                </div>
                <div className="space-y-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{offer.repay_type === 'EDI' ? 'EDI' : 'EMI'}</p>
                    <p className="font-semibold text-gray-900">₹{parseFloat(offer.emi.toString()).toLocaleString('en-IN')}</p>
                </div>
            </div>

            <div className="flex items-center justify-end md:pl-4 md:border-l border-gray-100">
                <div className={`
                    w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                    ${offer.rejected_by_le ? 'border-gray-200 bg-gray-100' : isSelected ? 'border-primary bg-primary' : 'border-gray-300'}
                    `}>
                    {isSelected && !offer.rejected_by_le && (
                        <div className="w-2.5 h-2.5 rounded-full bg-white" />
                    )}
                </div>
            </div>

            {/* Provisional KFS Link - Absolute positioned or flexible */}
            {!offer.rejected_by_le && (
                <div className="md:absolute md:bottom-2 md:right-16 text-right mt-2 md:mt-0">
                    <span
                        onClick={(e) => {
                            // Open in new tab
                            window.open(`/kfs/${offer.id}?amount=${offer.loan_amount}&tenure=${offer.tenure}`, '_blank');
                        }}
                        className="text-[10px] text-blue-500 hover:text-blue-700 underline cursor-pointer flex items-center justify-end gap-1"
                    >
                        + Show Details (Provisional KFS)
                    </span>
                </div>
            )}

            {offer.processing_fee && !offer.rejected_by_le && (
                <Badge variant="secondary" className="absolute -top-3 right-4 shadow-sm text-xs font-normal bg-gray-100 text-gray-600">
                    Processing Fee: ₹{parseFloat(offer.processing_fee.toString()).toLocaleString('en-IN')}
                </Badge>
            )}
        </div>
    );
};

export default LoanOfferCard;
